import * as THREE from 'three';
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader.js';

import { Axial, axialKey, axialToWorld, neighbor } from '../map/HexCoordinates';
import { SAIL_STEP_SECONDS } from '../map/Movement';
import { HEX_SIZE, elevationToY } from '../map/HexMetrics';
import { HexCellData, waterLevel } from '../map/HexSurface';
import { flatten, type PropModel } from '../props/PropModels';
import type { Anchor } from '../buildings/Anchors';
// Ten sam łuk, którym idzie pionek: krok wychodzi tam, gdzie poprzedni przyszedł.
import { alongStep, bendOf, headingOnStep } from '../players/PlayerMarkers';
import { Glide } from '../map/Glide';

/**
 * Faza kołysania z identyfikatora: ta sama dla tego statku po każdym
 * przeładowaniu, a inna niż u sąsiada — dwa kadłuby kiwające się zgodnie
 * wyglądają jak jedna rzecz.
 */
function phaseOf(id: string): number {
  let hash = 0;
  for (const character of id) {
    hash = (hash * 31 + character.charCodeAt(0)) % 100003;
  }
  return (hash / 100003) * HEAVE_PERIOD;
}
import type { ShipView } from '../../network/Ships';

/**
 * Statki na wodzie.
 *
 * **Bliźniak `ConvoyMarkers`, z jedną różnicą, która dziś jest całą treścią
 * tego pliku: statek nie płynie.** Stoi tam, gdzie go zbudowano, więc nie ma tu
 * ani wygładzania ruchu, ani czasu przejścia — dojdzie to razem z żeglugą i tą
 * samą drogą, którą ma je wóz.
 *
 * **Stoi na lustrze, nie na dnie.** Pole wody niesie głębokość, a statek pływa
 * po powierzchni — to samo pytanie, które przy moście rozstrzyga `deckY`, tylko
 * bez pokładu.
 */

/** Zastępczy kadłub, dopóki nie ma pliku: łódka z pudełka, nie klocek. */
const HULL_LENGTH = HEX_SIZE * 0.72;
const HULL_WIDTH = HEX_SIZE * 0.3;
const HULL_HEIGHT = HEX_SIZE * 0.22;

/**
 * Jak wysoki jest statek na wodzie, w jednostkach świata.
 *
 * Mniej niż budynek (`MODEL_HEIGHT` to 1,8 × `HEX_SIZE`): statek ma się mieścić
 * między brzegami, a nie zasłaniać pole, na którym stoi.
 */
const SHIP_HEIGHT = HEX_SIZE * 0.9;

/** Najkrótsze przejście — podłoga na wypadek terenu, o którym nic nie wiemy. */
const SHORTEST_STEP = 0.05;

/**
 * Jak blisko krawędzi pola cumuje pusty statek, w ułamku promienia heksa.
 *
 * Nie do samej krawędzi: kadłub ma szerokość, a przy jedynce połowa wystawałaby
 * na sąsiednie pole.
 */
const MOORED_OUT = 0.5;

/** Jak mocno kołysze się płynący statek — patrz „Falowanie". */
const WAVE_HEAVE = HEX_SIZE * 0.02;
const WAVE_ROLL = 0.05;
/** Okresy w sekundach; dwa różne, żeby ruch nie wyglądał na zapętlony. */
const HEAVE_PERIOD = 2.7;
const ROLL_PERIOD = 3.9;

/** Jeden wektor na wszystkie styczne w klatce — liczy się co statek. */
const HEADING = new THREE.Vector3();

/**
 * O ile obrócić bryłę, żeby dziób patrzył w stronę marszu.
 *
 * **Model leży wzdłuż osi X, dziobem w −X.** Przy zerze statek płynął tyłem —
 * sprawdzone w grze. To ta sama poprawka i ta sama zasada, co `MODEL_FACING`
 * u rycerza: gdyby przyszła bryła leżąca inaczej, zmienia się ta jedna stała,
 * a nie każde miejsce, które liczy kąt.
 */
const MODEL_FACING = Math.PI;

/**
 * Jak wysokie jest drzewce na maszcie, w jednostkach świata.
 *
 * Wyprowadzone z wysokości statku, a nie wpisane: bryła większa albo mniejsza
 * ma nieść sztandar w swojej skali.
 */
const STAFF_HEIGHT = SHIP_HEIGHT * 0.5;

interface Marker {
  cell: Axial;
  object: THREE.Object3D;
  /** Dokąd płynie ten krok i skąd się zaczął — do wygładzenia. */
  target: THREE.Vector3;
  from: THREE.Vector3;
  /** Punkt sterujący łuku tego kroku i kierunek wejścia — patrz `bendOf`. */
  control: THREE.Vector3;
  enter: THREE.Vector3;
  /**
   * Sunięcie po całej trasie — patrz `Glide`. **Tylko własny statek**: cudze
   * kroki przychodzą po jednym i nie ma z czego przewidywać.
   */
  glide?: Glide;
  /**
   * Ile ostatni tor miał trwać i ile trwał — **zostaje po jego zgaszeniu**.
   *
   * Pomiar z serwera przychodzi po dojściu, czyli wtedy, gdy toru już nie ma;
   * bez tego nie byłoby czego z nim zestawić.
   */
  travel?: { planned: number; took: number };
  /** Ile sekund trwa ten krok — dokładnie tyle, ile na serwerze. */
  duration: number;
  elapsed: number;
  /** Czyj jest — do menu i do sztandaru na maszcie. */
  owner: string;
  /** Czy ma załogę: pusty stoi przy krawędzi, z załogą wypływa na środek. */
  crewed: boolean;
  /**
   * Przy której krawędzi cumuje — **wybrane raz i zapamiętane**.
   *
   * Strona bierze się z pierwszego sąsiada, który jest lądem, a klient zna
   * tylko teren, który zdążył dojechać: przy innym przybliżeniu wczytany jest
   * inny kawałek mapy, więc „pierwszy ląd" bywał **innym polem** i kadłub
   * przeskakiwał na drugą stronę heksa. Brzeg się nie przesuwa, więc i strona
   * nie ma prawa.
   */
  mooredAt?: THREE.Vector3;
  /** Ile sekund ten statek już się kołysze — własna faza, żeby nie kiwały się zgodnie. */
  wave: number;
  /** Ile sekund budowy zostało, gdy serwer to powiedział. Zero na gotowym. */
  readyIn: number;
  /** Ile trwa budowa w całości — mianownik paska. */
  takes: number;
  /** Kiedy ten klient to usłyszał: stąd odlicza własnym zegarem. */
  since: number;
}

export class ShipMarkers {
  readonly object = new THREE.Group();

  private readonly markers = new Map<string, Marker>();
  private model?: PropModel;
  /**
   * Skąd wziąć sztandar właściciela i gdzie go na bryle postawić.
   *
   * **Punkt przychodzi z `models.json`**, jak przy budynkach: maszt zaznacza się
   * na stronie Modele, a bryła bez punktu po prostu płynie bez sztandaru — tak
   * samo, jak budynek bez punktu go nie nosi.
   */
  private bannerSource?: (owner: string, version: number, height: number) => Promise<THREE.Object3D | null>;
  private bannerPoint?: Anchor;
  private readonly flags = new Map<string, THREE.Object3D>();
  /** Zastępczy kadłub — jeden na wszystkie statki, jak sylwetki budynków. */
  private readonly hull = new THREE.BoxGeometry(HULL_LENGTH, HULL_HEIGHT, HULL_WIDTH);
  private readonly paint = new THREE.MeshStandardMaterial({
    color: 0x8a6a45,
    roughness: 0.9,
  });

  constructor(private readonly world: Map<number, HexCellData>) {
    this.object.name = 'ships';
  }

  /**
   * Wczytaj bryłę statku.
   *
   * **Brak pliku nie jest awarią** — statek zostaje przy zastępczym kadłubie,
   * dokładnie tak, jak rodzaj budynku bez modelu zostaje przy swojej sylwetce.
   */
  async useModel(): Promise<void> {
    try {
      const gltf = await new GLTFLoader().loadAsync('/models/ship-1.glb');
      this.model = flatten('ship-1', gltf.scene);
      // Bryły, które już stoją, dostają nową postać: plik przyjeżdża osobnym
      // żądaniem i bywa wolniejszy od pierwszego statku w zasięgu.
      for (const [id, marker] of [...this.markers]) {
        const { cell, readyIn, takes, since } = marker;
        this.remove(id);
        this.set({
          id,
          owner: marker.owner,
          crewed: marker.crewed,
          q: cell.q,
          r: cell.r,
          // Odliczanie idzie dalej od tej samej chwili, a nie od nowa: bryła
          // przyjechała później niż statek i nie jest o nim żadną wiadomością.
          readyIn: Math.max(0, readyIn - (performance.now() - since) / 1000),
          takes,
        });
      }
    } catch (error) {
      console.warn('bryła statku:', error);
    }
  }

  /** Skąd brać sztandary i gdzie je stawiać — patrz `bannerSource`. */
  useBanners(
    source: (owner: string, version: number, height: number) => Promise<THREE.Object3D | null>,
    point: Anchor | undefined
  ): void {
    this.bannerSource = source;
    this.bannerPoint = point;
  }

  /** Gdzie ten statek teraz jest i jak daleko mu do gotowości. */
  set(ship: ShipView): void {
    const at = { q: ship.q, r: ship.r };
    const held = this.markers.get(ship.id);
    if (held) {
      const moved = held.cell.q !== at.q || held.cell.r !== at.r;
      held.cell = at;
      held.owner = ship.owner;
      held.crewed = ship.crewed;
      held.readyIn = ship.readyIn;
      held.takes = ship.takes;
      held.since = performance.now();

      // **Dopłynięcie i cumowanie to ruch, a nie przestawienie.** Obraz idzie
      // własnym torem i na końcu trasy różni się od serwera o **najwyżej
      // jeden krok** — posadzenie bryły na nowo było więc widać jako przeskok
      // tuż przed dopłynięciem. To samo dotyczy powrotu na cumy: kadłub
      // odsuwa się do krawędzi pola, a odsunięcie w mgnieniu oka czyta się jak
      // szarpnięcie. Jedno i drugie przechodzi się teraz płynnie.
      // Pole się zmieniło — strona cumowania należała do poprzedniego.
      if (moved) held.mooredAt = undefined;
      this.glideTo(held, this.seatFor(held));
      return;
    }

    const object = this.build();
    this.object.add(object);
    const seat = this.seatOf(at);
    const marker: Marker = {
      cell: at,
      object,
      owner: ship.owner,
      crewed: ship.crewed,
      // Faza z identyfikatora, a nie z zegara: dwa statki obok siebie mają
      // kiwać się osobno, a ten sam statek tak samo po przeładowaniu strony.
      wave: phaseOf(ship.id),
      target: seat.clone(),
      from: seat.clone(),
      control: seat.clone(),
      enter: new THREE.Vector3(),
      duration: 0,
      elapsed: 0,
      readyIn: ship.readyIn,
      takes: ship.takes,
      since: performance.now(),
    };
    this.markers.set(ship.id, marker);
    this.seat(marker);
    this.raiseBanner(ship.id, marker, ship.banner);
  }

  /**
   * Postaw sztandar właściciela na maszcie.
   *
   * Ta sama droga, co przy budynkach: punkt z bryły, płat z serwera, a między
   * pytaniem a odpowiedzią statek mógł zniknąć — wtedy nie ma czego wieszać.
   */
  private raiseBanner(id: string, marker: Marker, version: number | undefined): void {
    this.flags.get(id)?.removeFromParent();
    this.flags.delete(id);

    const point = this.bannerPoint;
    if (!point || version === undefined || !this.bannerSource || !this.model) return;

    const mast = marker.object;
    void this.bannerSource(marker.owner, version, STAFF_HEIGHT).then((flag) => {
      if (!flag || this.markers.get(id)?.object !== mast) return;
      this.flags.get(id)?.removeFromParent();
      // Punkt jest w przestrzeni znormalizowanej bryły, a bryła stoi w grupie
      // przeskalowanej do `SHIP_HEIGHT` — więc punkt jedzie tą samą skalą.
      flag.position.set(...point);
      mast.add(flag);
      this.flags.set(id, flag);
    });
  }

  /**
   * Statki, które jeszcze się budują, i jak daleko im do końca.
   *
   * Ten sam kształt, co budowy budynków i mostów, bo pasek nad nimi jest jeden
   * (`BuildProgress`). Liczone **własnym zegarem** od tego, co powiedział
   * serwer — a statek, który się skończył, po prostu wypada z listy i nikomu
   * nie trzeba mówić, że gotowy.
   */
  raising(): { id: string; position: THREE.Vector3; progress: number }[] {
    const now = performance.now();
    const bars: { id: string; position: THREE.Vector3; progress: number }[] = [];

    for (const [id, marker] of this.markers) {
      if (marker.takes <= 0) continue;
      const left = Math.max(0, marker.readyIn - (now - marker.since) / 1000);
      if (left <= 0) continue;

      bars.push({
        id,
        position: new THREE.Vector3(
          marker.object.position.x,
          marker.object.position.y + SHIP_HEIGHT,
          marker.object.position.z
        ),
        progress: 1 - left / marker.takes,
      });
    }

    return bars;
  }

  remove(id: string): void {
    const marker = this.markers.get(id);
    if (!marker) return;
    this.flags.get(id)?.removeFromParent();
    this.flags.delete(id);
    this.object.remove(marker.object);
    this.markers.delete(id);
  }

  /**
   * Statek przeszedł na sąsiednie pole.
   *
   * **Czas, a nie prędkość** — tak samo, jak idzie pionek i jedzie wóz: krok
   * trwa dokładnie tyle, ile trwał na serwerze, więc statek dopływa na pole
   * w tej samej chwili, w której serwer go tam postawił.
   */
  moveTo(id: string, at: Axial): void {
    const marker = this.markers.get(id);
    if (!marker) return;

    // Potwierdzenie, nie zezwolenie: **obrazu nie ruszamy**, bo idzie własnym
    // torem — ale **pole zapamiętujemy**. `cell` znaczy „gdzie serwer stawia ten
    // kadłub", i to jest jedyne znaczenie, które da się utrzymać: tor wie, gdzie
    // bryłę **rysuje**, a te dwie rzeczy różnią się w trakcie rejsu o krok.
    if (marker.glide?.running) {
      if (marker.cell.q !== at.q || marker.cell.r !== at.r) marker.mooredAt = undefined;
      marker.cell = at;
      return;
    }

    // Kierunek wyjścia z kroku, który się kończy — patrz ten sam rachunek
    // w `PlayerMarkers.moveTo`: następna wiadomość bywa szybsza niż klatka,
    // w której poprzedni łuk dobiegł końca.
    if (marker.elapsed > 0) {
      const exit = HEADING.copy(marker.target).sub(marker.control);
      if (exit.lengthSq() > 1e-8) marker.enter.copy(exit).normalize();
    }

    marker.from = marker.object.position.clone();
    marker.cell = at;
    marker.elapsed = 0;
    marker.duration = Math.max(SHORTEST_STEP, SAIL_STEP_SECONDS);
    // Płynący nie cumuje: idzie środkiem pola do środka następnego.
    marker.mooredAt = undefined;
    marker.target = this.seatFor(marker);
    // Łuk, nie odcinek: kadłub wychodzi z pola tam, gdzie w nie wpłynął, więc
    // skręca, zamiast obracać się w miejscu na każdym środku.
    bendOf(marker.from, marker.target, marker.enter, marker.control);
  }

  /**
   * Przesuń statki o klatkę — krok i **falowanie**.
   *
   * Kołysze się ten, kto płynie: statek przy pomoście stoi. Ruch jest czystym
   * dodatkiem do pozycji i obrotu, bez własnego stanu poza fazą — dzięki temu
   * nie ma czego resetować przy każdym kroku i nic się nie kumuluje.
   */
  update(delta: number): void {
    for (const marker of this.markers.values()) {
      // Sunięcie po całej trasie wygrywa z wygładzaniem kroku.
      if (marker.glide?.running) {
        if (marker.glide.advance(delta)) {
          marker.glide.point(marker.object.position);
          const heading = marker.glide.tangent(HEADING);
          if (heading.lengthSq() > 1e-8) {
            marker.object.rotation.y = Math.atan2(-heading.z, heading.x) + MODEL_FACING;
          }
          const surface = this.seatOf(marker.cell).y;
          marker.object.position.y = surface;
          this.rock(marker, delta);
          continue;
        }
        marker.travel = marker.glide.timing;
        marker.glide = undefined;
        // Tam, gdzie tor się skończył — łącznie z polem, bo `cell` było celem
        // trasy, a nie polem pod bryłą.
        this.standStill(marker);
      }

      if (marker.elapsed < marker.duration) {
        marker.elapsed = Math.min(marker.duration, marker.elapsed + delta);
        const part = marker.duration > 0 ? marker.elapsed / marker.duration : 1;
        alongStep(marker.from, marker.control, marker.target, part, marker.object.position);

        // Dziobem tam, gdzie naprawdę płynie — model leży wzdłuż +X, więc kąt
        // liczy się od tej osi, a nie od +Z jak przy budynkach.
        const heading = headingOnStep(
          marker.from,
          marker.control,
          marker.target,
          part,
          HEADING
        );
        if (heading.lengthSq() > 1e-8) {
          marker.object.rotation.y = Math.atan2(-heading.z, heading.x) + MODEL_FACING;
        }
      } else {
        marker.object.position.copy(marker.target);
      }

      this.rock(marker, delta);
    }
  }

  /**
   * Falowanie: unoszenie i przechył, o ile jest kogo kołysać.
   *
   * Osobno, bo pytają o nie dwie drogi — sunięcie po trasie i wygładzanie kroku
   * — a dwie kopie tego rachunku rozjechałyby się przy pierwszej zmianie.
   */
  private rock(marker: Marker, delta: number): void {
    if (!marker.crewed) {
      marker.object.rotation.z = 0;
      return;
    }

    marker.wave += delta;
    const heave = Math.sin((marker.wave / HEAVE_PERIOD) * Math.PI * 2);
    const roll = Math.sin((marker.wave / ROLL_PERIOD) * Math.PI * 2);
    marker.object.position.y += heave * WAVE_HEAVE;
    // Przechył wokół osi kadłuba: model leży wzdłuż +X, więc kołysze się wokół
    // niej — burta w dół i z powrotem, a nie dziób w górę.
    marker.object.rotation.z = roll * WAVE_ROLL;
  }

  /**
   * Prowadź ten statek **całą trasą**, zamiast od kroku do kroku.
   *
   * Każdy krok po wodzie trwa tyle samo — morze nie ma lasu ani kamienia — więc
   * czasy są jedną liczbą powtórzoną tyle razy, ile pól.
   */
  follow(id: string, cells: Axial[]): void {
    const marker = this.markers.get(id);
    if (!marker) return;

    if (cells.length === 0) {
      marker.glide = undefined;
      return;
    }

    const glide = marker.glide ?? new Glide();
    glide.follow(
      marker.object.position.clone().setY(0),
      cells.map((cell) => this.seatOf(cell).setY(0)),
      cells.map(() => Math.max(SHORTEST_STEP, SAIL_STEP_SECONDS))
    );
    marker.glide = glide;
    // Nowa trasa kasuje pomiar starej: dwie liczby z dwóch różnych tras nie są
    // porównaniem niczego.
    marker.travel = undefined;
  }

  /**
   * Przejdź na nowe miejsce **płynnie**, zamiast się na nim postawić.
   *
   * Krótkie przejście, bo to nie jest krok po mapie, tylko dociągnięcie obrazu
   * do tego, co mówi serwer — albo odsunięcie do krawędzi przy cumowaniu.
   */
  private glideTo(marker: Marker, target: THREE.Vector3): void {
    marker.from.copy(marker.object.position);
    marker.control.copy(marker.object.position).lerp(target, 0.5);
    marker.target.copy(target);
    marker.duration = Math.max(SHORTEST_STEP, SAIL_STEP_SECONDS / 2);
    marker.elapsed = 0;
  }

  /** Ile tor tej bryły miał trwać i ile trwał — do zestawienia z serwerem. */
  timing(id: string): { planned: number; took: number } | undefined {
    return this.markers.get(id)?.travel;
  }

  /**
   * Przestań sunąć — statek dopłynął albo rejs został odwołany.
   *
   * **Zostaw znacznik tam, gdzie bryła naprawdę jest.** Zgaszony tor zostawiał
   * `from`, `target` i `cell` w stanie sprzed wyruszenia: `cell` było **celem
   * trasy**, a niedokończone przejście — początkiem. Spacja w połowie rejsu
   * rzucała przez to statek najpierw na punkt startu (dokończenie tamtego
   * przejścia), a potem na koniec trasy (pierwsze przeliczenie wysokości),
   * choć mapa i serwer wiedziały, gdzie kadłub stoi.
   */
  unfollow(id: string): void {
    const marker = this.markers.get(id);
    if (!marker?.glide) return;
    marker.glide = undefined;
    // **Dociągnięcie do serwera, a nie zamrożenie obrazu.** Rejs przerwany
    // w połowie — zejściem na ląd albo Spacją — zostawiał kadłub tam, gdzie
    // akurat był narysowany, a to bywa **o pole za daleko** od miejsca, w którym
    // serwer go zacumował. Poprawka przychodziła wtedy wcześniej niż to
    // zgaszenie i przepadała pod nim; widać to było dopiero po odświeżeniu gry,
    // które czyta pola od nowa.
    this.glideTo(marker, this.seatFor(marker));
  }

  /**
   * Zatrzymaj bryłę tam, gdzie jest: pole spod niej, przejście zamknięte.
   *
   * Pole liczy się **z położenia bryły**, a nie z tego, co serwer potwierdził
   * ostatnio — bo to bryłę widać, a różnica bywa o krok.
   */
  private standStill(marker: Marker): void {
    const spot = marker.object.position;
    marker.from.copy(spot);
    marker.control.copy(spot);
    marker.target.copy(spot);
    marker.elapsed = marker.duration;
  }

  /** Czyj jest ten statek. */
  ownerOf(id: string): string | undefined {
    return this.markers.get(id)?.owner;
  }

  /** Czy ten statek jeszcze się buduje — liczone własnym zegarem. */
  building(id: string): boolean {
    const marker = this.markers.get(id);
    if (!marker) return false;
    return marker.readyIn - (performance.now() - marker.since) / 1000 > 0;
  }

  /** Gdzie stoi ten statek. */
  cellOf(id: string): Axial | undefined {
    return this.markers.get(id)?.cell;
  }

  /**
   * Gdzie bryła statku jest **w tej chwili**, w jednostkach świata.
   *
   * Nie środek pola: pytają o to kamera i podpis, a jedno i drugie ma iść za
   * tym, co widać — czyli za torem i falowaniem, a nie za ostatnim
   * potwierdzeniem.
   */
  spotOf(id: string): THREE.Vector3 | undefined {
    return this.markers.get(id)?.object.position;
  }

  /** Który statek stoi na tym polu — do wskazywania kursorem. */
  whoIsAt(cell: Axial): string | undefined {
    for (const [id, marker] of this.markers) {
      if (marker.cell.q === cell.q && marker.cell.r === cell.r) return id;
    }
    return undefined;
  }

  /** Woda pod statkami mogła właśnie dojechać. */
  refresh(): void {
    // Sunącego nie przestawia się: `cell` jest u niego celem trasy, a nie polem,
    // na którym stoi — ta sama pułapka, co przy pionkach.
    for (const marker of this.markers.values()) {
      if (!marker.glide?.running) this.seat(marker);
    }
  }

  clear(): void {
    for (const id of [...this.markers.keys()]) this.remove(id);
  }

  dispose(): void {
    this.clear();
    this.hull.dispose();
    this.paint.dispose();
  }

  private build(): THREE.Object3D {
    if (!this.model) return new THREE.Mesh(this.hull, this.paint);

    // Model przychodzi znormalizowany do wysokości jeden — jak drzewa i budynki
    // — więc skala jest tu jedną liczbą, a nie mierzeniem pliku po raz drugi.
    const group = new THREE.Group();
    for (const part of this.model.parts) {
      const mesh = new THREE.Mesh(part.geometry, part.material);
      mesh.castShadow = true;
      group.add(mesh);
    }
    group.scale.setScalar(SHIP_HEIGHT);
    return group;
  }

  private seat(marker: Marker): void {
    const spot = this.seatFor(marker);
    marker.object.position.copy(spot);
    marker.target = spot.clone();
    marker.from = spot.clone();
    marker.elapsed = marker.duration;
  }

  /**
   * Gdzie w świecie stoi statek na tym polu.
   *
   * **Lustro wody, a nie dno**: pole wody niesie głębokość, a statek pływa po
   * powierzchni — to samo pytanie, które przy moście rozstrzyga `deckY`.
   */
  private seatOf(cell: Axial): THREE.Vector3 {
    const { x, z } = axialToWorld(cell.q, cell.r);
    const data = this.world.get(axialKey(cell.q, cell.r));
    const surface = data ? waterLevel(data) : undefined;
    return new THREE.Vector3(x, elevationToY(surface ?? 0), z);
  }

  /**
   * Gdzie ten statek ma stać: na środku pola, a bez załogi — przy krawędzi od
   * strony lądu.
   *
   * **Strona wybierana jest raz i zostaje** (`mooredAt`): liczona za każdym
   * razem zmieniała się razem z tym, ile terenu zdążyło dojechać. Bez lądu
   * wokół — a statek może stać na pełnym morzu — kadłub zostaje na środku, i to
   * jest uczciwsze niż wybieranie krawędzi, za którą nic nie ma.
   */
  private seatFor(marker: Marker): THREE.Vector3 {
    const spot = this.seatOf(marker.cell);
    if (marker.crewed) return spot;

    marker.mooredAt ??= this.shoreSide(marker.cell) ?? undefined;
    if (!marker.mooredAt) return spot;
    return spot.addScaledVector(marker.mooredAt, HEX_SIZE * MOORED_OUT);
  }

  /**
   * W którą stronę jest brzeg — wektor jednostkowy ku pierwszemu sąsiadowi,
   * który jest lądem. Deterministycznie po kolei krawędzi, żeby ten sam statek
   * cumował zawsze tak samo.
   */
  private shoreSide(cell: Axial): THREE.Vector3 | null {
    const here = axialToWorld(cell.q, cell.r);
    for (let edge = 0; edge < 6; edge += 1) {
      const side = neighbor(cell, edge);
      const data = this.world.get(axialKey(side.q, side.r));
      if (!data || waterLevel(data) !== undefined) continue;
      const there = axialToWorld(side.q, side.r);
      return new THREE.Vector3(there.x - here.x, 0, there.z - here.z).normalize();
    }
    return null;
  }
}
